/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {useContext, useState} from 'react';
import {AppContext} from '../../misc/appContext';
import {NO_REPLY_FLEX_KEY} from '../../../util/constants';
import {nullify, NumberString, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import Account from '../../../models/accountModel';
import {createErrorMessage} from '../../../util/utils';
import EmailSettings from './emailSettings';
import {PermissionCallback} from '../../../util/pageChanger';
import PageChangePermission from '../../misc/pageChangePermission';
import {trackPlatformAction} from '../../../util/analytics';
import {useTranslation} from 'react-i18next';

export default function EmailSettingsController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const emailMaxProductNameLength = account?.settings.emailMaxProductNameLength;
    let productLength: string;
    if (emailMaxProductNameLength) productLength = emailMaxProductNameLength.toString();
    else productLength = '';
    const [noReplyTracking, setNoReplyTracking] = useState(account?.getFlex(NO_REPLY_FLEX_KEY) === true);
    const [sendToCanceled, setSendToCanceled] = useState(account?.settings?.sendCanceledInvoices ?? false);
    const [maxProductLength, setMaxProductLength] = useState(productLength);
    const [includeUBLAttachment, setIncludeUBLAttachment] = useState(account?.settings?.includeUBLAttachment ?? false);
    const [loading, setLoading] = useState(false);
    const [changed, setChanged] = useState(false);

    function onChangeSendToCanceled() {
        setSendToCanceled(!sendToCanceled);
        setChanged(true);
    }

    function onChangeNoReplyTracking(useTracking: boolean) {
        setNoReplyTracking(useTracking);
        setChanged(true);
    }

    function onChangeMaxProductLength(productLength: NumberString) {
        setMaxProductLength(productLength.stringValue);
        setChanged(true);
    }

    function onChangeIncludeUBLAttachment(value: boolean) {
        setIncludeUBLAttachment(value);
        setChanged(true);
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        if (changed)
            onSubmit(() => {
                callback(id, true);
            })();
        else callback(id, true);
    }

    function onSubmit(callback?: VoidFunction) {
        return function () {
            const {account} = context.accountContext;
            if (!account) return;
            setLoading(true);
            account.setFlex(NO_REPLY_FLEX_KEY, noReplyTracking);
            const maxLength = nullify(maxProductLength);
            account.settings.emailMaxProductNameLength = !maxLength ? null : Number(maxLength);
            account.settings.sendCanceledInvoices = sendToCanceled;
            account.settings.includeUBLAttachment = includeUBLAttachment;
            account.update(
                undefined,
                undefined,
                undefined,
                (account: Account, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        trackPlatformAction('settings.emails', 'save_settings');
                        context.onChangeContext({account});
                        showSnackbar(t('settings.email.settingsSaved', 'De instellingen zijn opgeslagen'));
                        setChanged(false);
                        callback?.();
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('settings.email.saveFailedMsg', 'Het opslaan van de instellingen is niet gelukt.')
                            ),
                            t('settings.email.saveFailedTitle', 'Opslaan mislukt')
                        );
                }
            );
        };
    }

    return (
        <>
            <EmailSettings
                onChangeMaxProductLength={onChangeMaxProductLength}
                onChangeSendToCanceled={onChangeSendToCanceled}
                onChangeNoReplyTracking={onChangeNoReplyTracking}
                onSubmit={onSubmit()}
                onChangeIncludeUBLAttachment={onChangeIncludeUBLAttachment}
                noReplyTracking={noReplyTracking}
                maxProductLength={maxProductLength}
                loading={loading}
                sendToCanceled={sendToCanceled}
                includeUBLAttachment={includeUBLAttachment}
                changed={changed}
            />
            <PageChangePermission
                changed={changed}
                onDeny={onDenyPageChange}
                denyText={t('settings.email.saveChanges', 'Ja, wijzigingen opslaan')}
                allowText={t('settings.email.leavePage', 'Nee, pagina verlaten')}
                allowSecondary={true}
            >
                {t(
                    'settings.email.unsavedChanges',
                    'De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?'
                )}
            </PageChangePermission>
        </>
    );
}
